/**
 * useAudioPlayer
 *
 * Why this exists: a plain `<audio>` element lies to you on a bad connection.
 * When the network stops delivering bytes, `waiting`/`stalled` fire late (or never)
 * and — especially for progressive MP3/AAC streams — the media clock keeps running
 * while the browser renders silence. The UI then shows the timer climbing and a
 * "playing" icon while zero new audio has been downloaded.
 *
 * This hook:
 *  - samples the element on a watchdog (`currentTime` vs wall clock vs buffered
 *    ranges) instead of trusting media events alone,
 *  - when data runs out it pauses the element and *pins the displayed timer*
 *    (rewinding the phantom clock drift) so the position never runs ahead of the
 *    audio we actually received,
 *  - resumes automatically as soon as enough audio is buffered again,
 *  - re-requests the stream (from the same position, bounded retries) when the
 *    download itself stalls,
 *  - understands offline/online so it waits for a reconnect instead of hammering.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

export type AudioStatus =
  | "loading" // first load of this source, nothing decoded yet
  | "playing"
  | "paused"
  | "buffering" // user wants to play, the network is behind
  | "recovering" // the stream request died, re-requesting from the same position
  | "offline" // no connection: waiting for it to come back
  | "error"; // gave up: needs a manual retry

export interface AudioPlayerApi {
  status: AudioStatus;
  /** true while sound is actually coming out of the element */
  isPlaying: boolean;
  /** first load of the current source (nothing audible yet) */
  isLoading: boolean;
  /** playback is interrupted because the network is behind */
  isBuffering: boolean;
  isOffline: boolean;
  duration: number;
  currentTime: number;
  /** furthest point already downloaded (seconds) */
  bufferedTime: number;
  /** seconds of audio downloaded in front of the playhead */
  bufferedAhead: number;
  /** how long the current buffering episode has lasted (seconds) */
  stallSeconds: number;
  /** recovery attempt (1-based) while status === "recovering" */
  attempt: number;
  error: string | null;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seekTo: (time: number) => void;
  retry: () => void;
}

export interface UseAudioPlayerOptions {
  src: string | null;
  /** start as soon as the (new) source can play — also applies to track changes */
  autoPlay?: boolean;
  onEnded?: () => void;
}

interface ClockSample {
  media: number;
  wall: number;
}

const HAVE_FUTURE_DATA = 3; // HTMLMediaElement.HAVE_FUTURE_DATA

const WATCHDOG_MS = 500;
const LEAD_CRITICAL = 0.35; // less than this much audio ahead == out of data
const LEAD_RESUME = 2; // resume once this much audio sits in front of the playhead
const STARVE_MS = 1200; // starvation must be sustained this long before we freeze
const SEEK_GRACE_MS = 5000; // after a seek, give the fetch some slack
const FETCH_STALL_MS = 20000; // no buffered growth for this long == dead request
const PLAY_THROTTLE_MS = 2000;
export const MAX_RECOVERY_ATTEMPTS = 4;
const RETRY_BASE_MS = 1500;
const RETRY_MAX_MS = 15000;

const GIVE_UP_MESSAGE =
  "Your connection is too unstable to load this audio. Tap retry to try again.";

const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(Math.max(value, lo), hi);

const online = (): boolean =>
  typeof navigator === "undefined" || navigator.onLine !== false;

/** Furthest point that is already downloaded. */
export const maxBufferedEnd = (audio: HTMLMediaElement): number => {
  let end = 0;
  for (let i = 0; i < audio.buffered.length; i++) {
    end = Math.max(end, audio.buffered.end(i));
  }
  return end;
};

/** End of the buffered range containing `position`, or -1 when it is not buffered. */
export const bufferedEndAt = (audio: HTMLMediaElement, position: number): number => {
  for (let i = 0; i < audio.buffered.length; i++) {
    if (
      position >= audio.buffered.start(i) - 0.01 &&
      position <= audio.buffered.end(i) + 0.01
    ) {
      return audio.buffered.end(i);
    }
  }
  return -1;
};

const describeMediaError = (err: MediaError): string => {
  switch (err.code) {
    case MediaError.MEDIA_ERR_NETWORK:
      return "The connection dropped while loading audio.";
    case MediaError.MEDIA_ERR_DECODE:
      return "Could not decode this audio file.";
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return "This audio format is not supported by your browser.";
    case MediaError.MEDIA_ERR_ABORTED:
      return "Audio loading was aborted.";
    default:
      return "Failed to load audio.";
  }
};

export const useAudioPlayer = (
  audioRef: React.RefObject<HTMLAudioElement>,
  { src, autoPlay = true, onEnded }: UseAudioPlayerOptions
): AudioPlayerApi => {
  const [status, setStatus] = useState<AudioStatus>("loading");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [bufferedTime, setBufferedTime] = useState(0);
  const [bufferedAhead, setBufferedAhead] = useState(0);
  const [stallSeconds, setStallSeconds] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [isOffline, setIsOffline] = useState(!online());
  const [error, setError] = useState<string | null>(null);

  // Props that must not re-run the media effect.
  const onEndedRef = useRef(onEnded);
  const autoPlayRef = useRef(autoPlay);
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);
  useEffect(() => {
    autoPlayRef.current = autoPlay;
  }, [autoPlay]);

  // --- playback bookkeeping (refs, because the watchdog needs live values) -----
  const wantsPlay = useRef(autoPlay); // user intent
  const everPlayed = useRef(false); // did we ever hear this source?
  const frozen = useRef(false); // paused by us because data ran out
  const frozenSince = useRef(0);
  const resumeAt = useRef(0); // position to continue from once data is back
  // last position proven audible (wall < 0 == never verified; performance.now()
  // can legitimately be 0, so it cannot double as "no sample")
  const verified = useRef<ClockSample>({ media: 0, wall: -1 });
  const lastSample = useRef<ClockSample>({ media: 0, wall: 0 });
  const lastBufferEnd = useRef(0);
  const noGrowthSince = useRef<number | null>(null);
  const starvingSince = useRef<number | null>(null);
  const seekGraceUntil = useRef(0);
  const restorePos = useRef<number | null>(null); // pending position after a reload
  const attempts = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallTicker = useRef<ReturnType<typeof setInterval> | null>(null);
  const bufferingSince = useRef(0);
  const lastPlayTry = useRef(-Infinity);
  const loadedSource = useRef<string | null>(null);
  const bufferedTimeState = useRef(0); // mirror of published buffer state
  const leadState = useRef(0);

  const controls = useRef<{
    play: () => void;
    pause: () => void;
    toggle: () => void;
    seek: (t: number) => void;
    retry: () => void;
  } | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const clearRetryTimer = () => {
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    };

    const durationOf = (): number =>
      Number.isFinite(audio.duration) ? audio.duration : 0;

    /** Count a buffering episode in the UI (spinner + "waiting for data" text). */
    const startStallClock = () => {
      if (bufferingSince.current === 0) bufferingSince.current = performance.now();
      if (!stallTicker.current) {
        stallTicker.current = setInterval(() => {
          setStallSeconds(
            Math.floor((performance.now() - bufferingSince.current) / 1000)
          );
        }, 1000);
      }
      setStatus((prev) =>
        prev === "buffering" ||
        prev === "recovering" ||
        prev === "offline" ||
        prev === "error" ||
        prev === "paused"
          ? prev
          : everPlayed.current
          ? "buffering"
          : "loading"
      );
    };

    const stopStallClock = () => {
      bufferingSince.current = 0;
      starvingSince.current = null;
      if (stallTicker.current) {
        clearInterval(stallTicker.current);
        stallTicker.current = null;
      }
      setStallSeconds(0);
    };

    /** Data in front of `position` is good enough to continue playback. */
    const canResumeAt = (position: number): boolean => {
      if (audio.readyState < HAVE_FUTURE_DATA) return false;
      const end = bufferedEndAt(audio, position);
      if (end < 0) return false;
      const dur = durationOf();
      const lead = end - position;
      const coversRest = dur > 0 && end >= dur - 0.3;
      const waited = performance.now() - frozenSince.current;
      // The longer we wait, the less lead we need — a weak connection that only
      // ever hands us small chunks should still make progress.
      return (
        lead >= LEAD_RESUME ||
        coversRest ||
        (waited > 8000 && lead >= 0.5) ||
        (waited > 20000 && lead >= 0.1)
      );
    };

    const attemptPlay = (force = false) => {
      const now = performance.now();
      if (!force && now - lastPlayTry.current < PLAY_THROTTLE_MS) return;
      lastPlayTry.current = now;
      const promise = audio.play();
      if (promise && typeof promise.then === "function") {
        promise.catch((err: any) => {
          if (err && err.name === "AbortError") return; // superseded by a new src / our pause()
          if (err && err.name === "NotAllowedError") {
            // browser blocked autoplay — stay paused, the user can tap play
            wantsPlay.current = false;
            setIsPlaying(false);
            setStatus("paused");
            return;
          }
          scheduleRetry();
        });
      }
    };

    const resumePlayback = () => {
      const position = resumeAt.current;
      frozen.current = false;
      frozenSince.current = 0;
      stopStallClock();
      attempts.current = 0;
      setAttempt(0);
      setError(null);
      noGrowthSince.current = null;
      lastBufferEnd.current = maxBufferedEnd(audio);
      lastSample.current = { media: position, wall: performance.now() };
      verified.current = { media: position, wall: performance.now() };
      if (!wantsPlay.current) {
        setIsPlaying(false);
        setStatus("paused");
        return;
      }
      if (Math.abs(audio.currentTime - position) > 0.3) {
        try {
          audio.currentTime = position;
        } catch {
          /* seeking can throw while the media engine is busy */
        }
      }
      attemptPlay(true); // the user is waiting: don't sit behind the throttle
    };

    /**
     * Data ran out: stop the element so the timer cannot keep climbing through
     * audio we never received, and rewind whatever clock drift the browser made up.
     */
    const freezeForBuffering = () => {
      if (frozen.current) return;
      const dur = durationOf();
      const stamp = verified.current;
      const furthestPlayable = maxBufferedEnd(audio);
      // We can only have *heard* audio that actually arrived: the position is the
      // last verified one plus the time spent playing since — capped by how much
      // audio was downloaded. Anything above that is a clock the browser made up.
      const inferred = stamp.wall >= 0
        ? Math.min(
            stamp.media + (performance.now() - stamp.wall) / 1000,
            Math.max(furthestPlayable, stamp.media)
          )
        : audio.currentTime;
      const truthful =
        dur > 0 ? clamp(inferred, 0, Math.max(0, dur - 0.05)) : Math.max(0, inferred);

      if (audio.currentTime - truthful > 1) {
        try {
          audio.currentTime = truthful;
        } catch {
          /* ignore */
        }
      }
      resumeAt.current = Math.min(audio.currentTime, truthful);
      frozen.current = true;
      frozenSince.current = performance.now();
      starvingSince.current = null;
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
      setIsPlaying(false);
      setCurrentTime(resumeAt.current);
      startStallClock();
      if (!online()) setStatus("offline");
    };

    /** Re-request the stream (the browser issues a fresh Range request) at our position. */
    const reload = () => {
      if (!online()) {
        markOffline();
        return; // don't spend a request on a connection we know is gone
      }
      const position = frozen.current
        ? resumeAt.current
        : Math.max(0, audio.currentTime || 0);
      restorePos.current = position > 0.5 ? position : null;
      resumeAt.current = position;
      frozen.current = true; // pin the displayed timer while the new request warms up
      if (!frozenSince.current) frozenSince.current = performance.now();
      noGrowthSince.current = null;
      lastBufferEnd.current = 0;
      startStallClock();
      // a fresh request is always worth showing (also clears a stale "error")
      setStatus(everPlayed.current ? "recovering" : "loading");
      try {
        audio.load();
      } catch {
        /* ignore */
      }
    };

    const markOffline = () => {
      setIsOffline(true);
      setStatus("offline");
    };

    /** Hard failure: stop trying, keep the position, ask the user. */
    const escalate = (message: string) => {
      clearRetryTimer();
      stopStallClock();
      frozen.current = true;
      resumeAt.current = Math.max(0, resumeAt.current || audio.currentTime || 0);
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
      wantsPlay.current = false;
      setIsPlaying(false);
      setIsLoading(false);
      setError(message);
      setStatus(online() ? "error" : "offline");
    };

    const beginAttempt = (): boolean => {
      if (attempts.current >= MAX_RECOVERY_ATTEMPTS) {
        escalate(GIVE_UP_MESSAGE);
        return false;
      }
      attempts.current += 1;
      setAttempt(attempts.current);
      return true;
    };

    /** The download stalled with no bytes moving: re-request immediately. */
    const recoverNow = () => {
      clearRetryTimer();
      if (!online()) {
        markOffline();
        return;
      }
      if (!beginAttempt()) return;
      setStatus("recovering");
      reload();
    };

    /** A media error came through: back off, then re-request. */
    const scheduleRetry = () => {
      clearRetryTimer();
      if (!online()) {
        markOffline();
        return;
      }
      if (!beginAttempt()) return;
      setStatus("recovering");
      const delay = Math.min(
        RETRY_MAX_MS,
        RETRY_BASE_MS * Math.pow(2, attempts.current - 1)
      );
      retryTimer.current = setTimeout(() => {
        retryTimer.current = null;
        reload();
      }, delay);
    };

    // --- watchdog ------------------------------------------------------------
    const tick = () => {
      const now = performance.now();
      const media = audio.currentTime || 0;
      const dur = durationOf();
      const furthest = maxBufferedEnd(audio);
      const endHere = bufferedEndAt(audio, media);
      const lead = endHere < 0 ? -1 : endHere - media;
      const dataComplete = dur > 0 && furthest >= dur - 0.25;

      // publish buffer info, but only when it moved enough to matter (the watchdog
      // would otherwise re-render twice a second for no reason)
      if (Math.abs(furthest - bufferedTimeState.current) > 0.25) {
        bufferedTimeState.current = furthest;
        setBufferedTime(furthest);
      }
      const leadDisplay = Math.max(0, lead);
      if (Math.abs(leadDisplay - leadState.current) > 0.5) {
        leadState.current = leadDisplay;
        setBufferedAhead(leadDisplay);
      }

      // is the download alive at all?
      if (furthest > lastBufferEnd.current + 0.05) {
        lastBufferEnd.current = furthest;
        noGrowthSince.current = null;
      } else if (noGrowthSince.current === null) {
        noGrowthSince.current = now;
      }

      if (!wantsPlay.current || audio.ended) return;

      const fetchLooksDead =
        !dataComplete &&
        noGrowthSince.current !== null &&
        now - noGrowthSince.current > FETCH_STALL_MS;

      // (A) already paused by us because data ran out
      if (frozen.current) {
        startStallClock();
        if (fetchLooksDead) {
          recoverNow();
          return;
        }
        if (canResumeAt(resumeAt.current)) resumePlayback();
        return;
      }

      // (B) we want to play but the element is paused (pending autoplay, seek, ...)
      if (audio.paused) {
        if (fetchLooksDead) {
          recoverNow();
          return;
        }
        if (lead < LEAD_CRITICAL) startStallClock();
        if (dataComplete || canResumeAt(audio.currentTime)) attemptPlay();
        return;
      }

      // (C) the element claims it is playing — verify that it actually is
      const sample = lastSample.current;
      const wallDt = (now - sample.wall) / 1000;
      const mediaDt = media - sample.media;
      lastSample.current = { media, wall: now };

      const nearEnd = dur > 0 && media >= dur - 1;
      const clockRate = wallDt > 0.25 ? mediaDt / wallDt : 1;
      const nothingAhead = lead < LEAD_CRITICAL && !dataComplete;
      const clockStopped = clockRate < 0.5 && !nearEnd; // browser already froze the clock
      const outOfData = nothingAhead && audio.readyState < HAVE_FUTURE_DATA;
      const starved = clockStopped || outOfData;

      if (now < seekGraceUntil.current) {
        if (nothingAhead) startStallClock();
        return;
      }

      if (!starved || nearEnd) {
        starvingSince.current = null;
        if (stallTicker.current) {
          stopStallClock();
          setStatus("playing");
        }
        return;
      }

      if (starvingSince.current === null) {
        starvingSince.current = now;
        startStallClock();
        return;
      }
      if (now - starvingSince.current < STARVE_MS) return;

      freezeForBuffering();
    };

    // --- media element events ------------------------------------------------
    const handleLoadedMetadata = () => {
      const dur = durationOf();
      if (dur) setDuration(dur);
      if (restorePos.current !== null) {
        const position = restorePos.current;
        restorePos.current = null;
        if (position > 0.5 && (!dur || position < dur - 0.2)) {
          try {
            audio.currentTime = position;
          } catch {
            /* ignore */
          }
        }
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      const dur = durationOf();
      if (dur) setDuration(dur);
      if (frozen.current) {
        if (canResumeAt(resumeAt.current)) resumePlayback();
        return;
      }
      if (wantsPlay.current && audio.paused) attemptPlay();
    };

    const handlePlaying = () => {
      // the only event we trust as "audio is really flowing"
      everPlayed.current = true;
      setIsLoading(false);
      setIsPlaying(true);
      setError(null);
      setIsOffline(false);
      attempts.current = 0;
      setAttempt(0);
      frozen.current = false;
      frozenSince.current = 0;
      starvingSince.current = null;
      noGrowthSince.current = null;
      lastSample.current = { media: audio.currentTime, wall: performance.now() };
      verified.current = { media: audio.currentTime, wall: performance.now() };
      stopStallClock();
      setStatus("playing");
    };

    const handleTimeUpdate = () => {
      if (frozen.current) return; // keep the displayed timer honest
      const position = audio.currentTime;
      setCurrentTime(position);
      if (!audio.paused && audio.readyState >= HAVE_FUTURE_DATA) {
        verified.current = { media: position, wall: performance.now() };
      }
    };

    // bytes arrived: re-evaluate right away instead of waiting for the watchdog
    const handleProgress = () => tick();

    const handleWaiting = () => {
      if (wantsPlay.current) startStallClock();
    };

    const handleStalled = () => {
      if (wantsPlay.current && !audio.paused) startStallClock();
    };

    const handlePlay = () => {
      setIsPlaying(true);
      // fresh sampling baseline: the first ticks after (re)starting must not be
      // compared against the clock we froze on
      lastSample.current = { media: audio.currentTime, wall: performance.now() };
      if (!frozen.current) setStatus("loading");
    };

    const handlePause = () => {
      if (frozen.current) return; // our own buffering pause — keep intent + spinner
      wantsPlay.current = false; // user (or another player on the page) stopped us
      setIsPlaying(false);
      stopStallClock();
      if (!audio.ended) setStatus("paused");
    };

    const handleEnded = () => {
      stopStallClock();
      setIsPlaying(false);
      wantsPlay.current = false;
      const dur = durationOf();
      if (dur) setCurrentTime(dur);
      setStatus("paused");
      if (onEndedRef.current) onEndedRef.current();
    };

    const handleError = () => {
      const mediaError = audio.error;
      if (!mediaError) return;
      if (mediaError.code === MediaError.MEDIA_ERR_ABORTED) return; // our own load()/src swap
      const message = describeMediaError(mediaError);
      setIsLoading(false);
      setIsPlaying(false);
      resumeAt.current = Math.max(0, resumeAt.current || audio.currentTime || 0);
      frozen.current = true; // pin the timer, we resume from here
      if (
        mediaError.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
        mediaError.code === MediaError.MEDIA_ERR_DECODE
      ) {
        escalate(message); // retrying something we cannot decode is pointless
        return;
      }
      if (!online()) {
        markOffline();
        return;
      }
      if (attempts.current >= MAX_RECOVERY_ATTEMPTS) escalate(message);
      else scheduleRetry(); // the status line already says "reconnecting (n/4)"
    };

    // --- connectivity --------------------------------------------------------
    const handleOffline = () => {
      setIsOffline(true);
      if (!wantsPlay.current) return;
      if (frozen.current) setStatus("offline");
      else freezeForBuffering();
    };

    const handleOnline = () => {
      setIsOffline(false);
      attempts.current = 0;
      setAttempt(0);
      setError(null);
      if (!wantsPlay.current) return;
      // whatever we had buffered is probably truncated — refetch from where we were
      frozen.current = true;
      seekGraceUntil.current = performance.now() + SEEK_GRACE_MS;
      setStatus("recovering");
      reload();
    };

    // --- imperative controls -------------------------------------------------
    const play = () => {
      clearRetryTimer();
      wantsPlay.current = true;
      setError(null);
      setIsOffline(false);
      attempts.current = 0;
      setAttempt(0);
      if (frozen.current) {
        if (canResumeAt(resumeAt.current)) resumePlayback();
        else reload(); // pressing play means "ask the network again now"
        return;
      }
      setStatus("loading");
      attemptPlay(true);
    };

    const pause = () => {
      clearRetryTimer();
      wantsPlay.current = false;
      frozen.current = false;
      frozenSince.current = 0;
      stopStallClock();
      setStatus("paused");
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
    };

    const toggle = () => {
      if (wantsPlay.current) pause();
      else play();
    };

    const seek = (time: number) => {
      const dur = durationOf();
      const target = clamp(time, 0, dur > 0 ? Math.max(0, dur - 0.05) : time);
      restorePos.current = null;
      frozen.current = false;
      frozenSince.current = 0;
      stopStallClock();
      noGrowthSince.current = null;
      lastBufferEnd.current = maxBufferedEnd(audio);
      seekGraceUntil.current = performance.now() + SEEK_GRACE_MS;
      verified.current = { media: target, wall: performance.now() };
      lastSample.current = { media: target, wall: performance.now() };
      setCurrentTime(target);
      try {
        audio.currentTime = target;
      } catch {
        /* ignore */
      }
      if (wantsPlay.current) attemptPlay(true);
    };

    const retry = () => {
      clearRetryTimer();
      attempts.current = 0;
      setAttempt(0);
      setError(null);
      setIsOffline(false);
      setIsLoading(!everPlayed.current);
      wantsPlay.current = true;
      frozen.current = true;
      seekGraceUntil.current = performance.now() + SEEK_GRACE_MS;
      setStatus(online() ? "recovering" : "offline");
      reload();
    };

    controls.current = { play, pause, toggle, seek, retry };

    // --- bind listeners + watchdog for this source ----------------------------
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("playing", handlePlaying);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("progress", handleProgress);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("stalled", handleStalled);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    const watchdog = setInterval(tick, WATCHDOG_MS);

    // --- (re)load the source --------------------------------------------------
    const absoluteSrc = src ? new URL(src, window.location.origin).href : null;
    if (absoluteSrc) {
      if (loadedSource.current !== absoluteSrc || audio.src !== absoluteSrc) {
        loadedSource.current = absoluteSrc;
        // fresh track: reset every bit of playback bookkeeping
        wantsPlay.current = autoPlayRef.current;
        everPlayed.current = false;
        frozen.current = false;
        frozenSince.current = 0;
        resumeAt.current = 0;
        restorePos.current = null;
        attempts.current = 0;
        verified.current = { media: 0, wall: -1 };
        lastSample.current = { media: 0, wall: 0 };
        lastBufferEnd.current = 0;
        noGrowthSince.current = null;
        starvingSince.current = null;
        seekGraceUntil.current = 0; // a fresh source is not a seek: watch it right away
        stopStallClock();
        clearRetryTimer();
        setError(null);
        setIsOffline(!online());
        setIsLoading(true);
        setIsPlaying(false);
        setStatus("loading");
        setDuration(0);
        setCurrentTime(0);
        setBufferedTime(0);
        setBufferedAhead(0);
        bufferedTimeState.current = 0;
        leadState.current = 0;
        setAttempt(0);
        audio.src = absoluteSrc;
        audio.load();
      }
    } else if (loadedSource.current !== null) {
      loadedSource.current = null;
      wantsPlay.current = false;
      frozen.current = false;
      stopStallClock();
      clearRetryTimer();
      try {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      } catch {
        /* ignore */
      }
      setIsPlaying(false);
      setIsLoading(false);
      setStatus("paused");
      setDuration(0);
      setCurrentTime(0);
      setBufferedTime(0);
      setBufferedAhead(0);
      bufferedTimeState.current = 0;
      leadState.current = 0;
    }

    if (wantsPlay.current && audio.paused && audio.readyState >= 1) {
      // React StrictMode re-mount, or the effect re-ran with the source already loaded
      attemptPlay();
    }

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("playing", handlePlaying);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("progress", handleProgress);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("stalled", handleStalled);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      clearInterval(watchdog);
      clearRetryTimer();
      stopStallClock();
      controls.current = null;
    };
    // refs are stable by convention; `src` is the only value that may re-load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Make sure navigating away stops the audio pipeline.
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (!audio) return;
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const play = useCallback(() => controls.current?.play(), []);
  const pause = useCallback(() => controls.current?.pause(), []);
  const togglePlay = useCallback(() => controls.current?.toggle(), []);
  const seekTo = useCallback((time: number) => controls.current?.seek(time), []);
  const retry = useCallback(() => controls.current?.retry(), []);

  return {
    status,
    isPlaying,
    isLoading,
    isBuffering: status === "buffering" || status === "recovering",
    isOffline,
    duration,
    currentTime,
    bufferedTime,
    bufferedAhead,
    stallSeconds,
    attempt,
    error,
    play,
    pause,
    togglePlay,
    seekTo,
    retry,
  };
};

/**
 * Short, honest status line for a buffering / recovering / offline player.
 * Returns null when there is nothing worth telling the user.
 */
export const describePlayback = (player: {
  status: AudioStatus;
  isOffline: boolean;
  isBuffering: boolean;
  bufferedAhead: number;
  stallSeconds: number;
  attempt: number;
}): { text: string; escalate: boolean } | null => {
  if (player.isOffline || player.status === "offline") {
    return { text: "No internet connection — playback resumes when it returns", escalate: player.stallSeconds > 10 };
  }
  if (player.status === "recovering") {
    return {
      text: `Stream stalled — reconnecting (attempt ${Math.max(1, player.attempt)}/${MAX_RECOVERY_ATTEMPTS})`,
      escalate: true,
    };
  }
  if (player.isBuffering || player.status === "buffering") {
    const ready = Math.max(0, Math.round(player.bufferedAhead));
    if (player.stallSeconds >= 15) {
      return {
        text: `Connection too slow — still trying (${player.stallSeconds}s)`,
        escalate: true,
      };
    }
    return {
      text: ready > 3 ? `Buffering — ${ready}s of audio ready` : "Buffering…",
      escalate: false,
    };
  }
  if (player.status === "loading" && player.stallSeconds >= 8) {
    return {
      text: "Connecting — this connection is slow",
      escalate: player.stallSeconds >= 20,
    };
  }
  return null;
};

export default useAudioPlayer;
